import { CURRENCY_CONFIGS } from '../constants';
import { type CurrencyKey, type RoiResults, type SliderInput } from '../types';
import OnboardingSection from './OnboardingSection';
import ProductivitySection from './ProductivitySection';
import styles from './Report.module.scss';
import ReportActionBar from './ReportActionBar';
import ReportSummary from './ReportSummary';
import RetentionSection from './RetentionSection';

interface ReportProps {
  results: RoiResults;
  inputs: SliderInput;
  currency: CurrencyKey;
  pricingPageUrl: string;
}

export default function Report({ results, inputs, currency, pricingPageUrl }: ReportProps) {
  const currencyConfig = CURRENCY_CONFIGS[currency];

  return (
    <div className={styles.reportContainer}>
      <ReportActionBar />
      <ReportSummary results={results} currency={currency} pricingPageUrl={pricingPageUrl} />

      <section className={styles.section} aria-label="Introduction">
        <div className={styles.sectionInner}>
          <div className={styles.splitLayout}>
            <div className={styles.introContent}>
              <h2 className={styles.sectionTitle}>Introduction</h2>
              <p className={styles.bodyText}>
                In a rapidly changing business environment, organizations are increasingly turning
                to an ever-wider global talent pool to find, train, and retain employees. Out of all
                the digital workplace tools available to businesses seeking to create empowered,
                connected workplaces capable of attracting and keeping these top performers, modern
                intranets occupy a unique position.
              </p>
              <p className={styles.bodyText}>
                A flexible, innovative intranet puts user needs first and has the power to connect,
                inform, and engage all global employees. Whether they&apos;re in-office, remote,
                frontline, or working in any other location, multichannel intranets give workers
                access to the productivity and communications tools they need.
              </p>
              <p className={styles.bodyText}>
                Like any other enterprise-grade software, however, the planning and implementation
                of a modern intranet requires buy-in from senior leaders who clearly understand the
                returns that their investment will bring.
              </p>
              <p className={styles.bodyText}>
                To demonstrate the potential return on investment (ROI) enterprises may realize by
                deploying an intranet, we have created this ROI calculator. The purpose of this
                calculator is to provide you and your decision makers with a financial framework to
                evaluate the potential financial impact of a new intranet project.
              </p>
              <p className={styles.bodyText}>
                Whether you&apos;re already building a business case, or are just interested to find
                out the potential ROI of a modern, packaged intranet, our ROI calculator breaks down
                the returns you may be able to achieve by increasing productivity, decreasing
                employee attrition, and improving onboarding and training efficiency.
              </p>
            </div>
            <div className={styles.introImages}>
              <div className={styles.introImagesRow}>
                <img
                  src={`${window.InteractRoiCalcData?.themeUri || ''}/assets/images/roi-img-1.jpg`}
                  alt="Person using a laptop"
                  className={styles.introImage}
                />
                <img
                  src={`${window.InteractRoiCalcData?.themeUri || ''}/assets/images/roi-img-2.jpg`}
                  alt="Person using a phone"
                  className={styles.introImage}
                />
              </div>
              <img
                src={`${window.InteractRoiCalcData?.themeUri || ''}/assets/images/roi-img-3.jpg`}
                alt="Group of engaged employees"
                className={styles.introImageFull}
              />
            </div>
          </div>
        </div>
      </section>

      <ProductivitySection
        results={results}
        inputs={inputs}
        currencyConfig={currencyConfig}
        currency={currency}
      />

      <OnboardingSection
        results={results}
        inputs={inputs}
        currencyConfig={currencyConfig}
        currency={currency}
      />

      <RetentionSection
        results={results}
        inputs={inputs}
        currencyConfig={currencyConfig}
        currency={currency}
      />

      <section className={styles.disclaimer} aria-label="Disclaimer">
        <div className={styles.sectionInner}>
          <h6 className={styles.disclaimerHeading}>Disclaimer</h6>
          <p className={styles.disclaimerText}>
            Interact Software provides this ROI calculator in good faith to help in the analysis of
            potential cost savings when purchasing and utilizing intranet software. Data provided
            for the calculation is the user&apos;s responsibility. We do not make any
            representations or warranties of any kind on the information provided. We also disclaim
            any liability or responsibility for the accuracy, correctness, or completeness of any
            information provided. Your results may vary from those provided by the ROI calculator.
          </p>
        </div>
      </section>
    </div>
  );
}
